import React, { useState, useEffect } from 'react';
import {
  HStack,
  Stat,
  StatNumber,
  StatLabel,
  Box,
  Icon,
  Tooltip,
  useColorModeValue,
  Flex,
  Divider,
  VStack,
} from '@chakra-ui/react';
import {
  FiPackage,
  FiShoppingBag,
  FiDollarSign,
  FiPercent,
  FiClock,
  FiCheckCircle
} from 'react-icons/fi';

const MinimalStatCard = ({ icon, label, number, helpText, accentColor = 'blue.500' }) => {
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Tooltip
      label={helpText}
      placement="top"
      hasArrow
      bg={accentColor}
    >
      <Stat size="sm" minW="120px" textAlign="center">
        <VStack spacing={1}>
          <HStack spacing={2} align="center" justify="center">
            <Icon as={icon} color={accentColor} boxSize={4} />
            <StatNumber fontSize="lg" fontWeight="bold">
              {number}
            </StatNumber>
          </HStack>
          <StatLabel fontSize="xs" color={labelColor} fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
            {label}
          </StatLabel>
        </VStack>
      </Stat>
    </Tooltip>
  );
};

const BundleStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const formatCurrency = (amount) => {
    const { symbol, position, debug } = qbndAdmin.currency;
    const formattedAmount = parseFloat(amount).toFixed(2);

    // Get currency code for fallback
    const currencyCode = debug?.currency_code || '';

    // Handle special characters and HTML entities in symbol
    let displaySymbol = symbol;

    // If symbol is HTML entity or special character, use currency code as fallback
    if (symbol.includes('&') || /[^\x20-\x7E]/.test(symbol)) {
      switch (currencyCode.toLowerCase()) {
        case 'usd':
          displaySymbol = '$';
          break;
        case 'eur':
          displaySymbol = '€';
          break;
        case 'gbp':
          displaySymbol = '£';
          break;
        case 'jpy':
          displaySymbol = '¥';
          break;
        default:
          // If no special handling, use the code itself
          displaySymbol = currencyCode;
      }
    }

    // Format based on position with proper spacing
    switch (position) {
      case 'left':
        return `${displaySymbol}${formattedAmount}`;
      case 'right':
        return `${formattedAmount}${displaySymbol}`;
      case 'left_space':
        return `${displaySymbol} ${formattedAmount}`;
      case 'right_space':
        return `${formattedAmount} ${displaySymbol}`;
      default:
        return `${displaySymbol}${formattedAmount}`;
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${qbndAdmin.root}qbnd/v1/bundles/stats`, {
        headers: {
          'X-WP-Nonce': qbndAdmin.nonce
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <Box
      py={3}
      px={6}
      bg={bgColor}
      borderRadius="2xl"
      border="1px"
      borderColor={borderColor}
      mb={6}
      maxW="fit-content"
      shadow="sm"
    >
      <Flex align="center" gap={6}>
        <MinimalStatCard
          icon={FiPackage}
          label="Total Bundles"
          number={stats?.total_bundles || 0}
          helpText={`${Math.abs(stats?.bundle_growth || 0).toFixed(1)}% growth this month`}
          accentColor="blue.500"
        />

        <Divider orientation="vertical" height="40px" borderColor={borderColor} />

        <MinimalStatCard
          icon={FiCheckCircle}
          label="Active"
          number={stats?.active_bundles || 0}
          helpText={`${((stats?.active_bundles / stats?.total_bundles) * 100).toFixed(1)}% of total bundles`}
          accentColor="green.500"
        />

        <Divider orientation="vertical" height="40px" borderColor={borderColor} />

        <MinimalStatCard
          icon={FiShoppingBag}
          label="Orders"
          number={stats?.total_sales || 0}
          helpText={`${Math.abs(stats?.sales_growth || 0).toFixed(1)}% vs last month`}
          accentColor="purple.500"
        />

        <Divider orientation="vertical" height="40px" borderColor={borderColor} />

        <MinimalStatCard
          icon={FiDollarSign}
          label="Revenue"
          number={formatCurrency(stats?.total_revenue || 0)}
          helpText={`From ${stats?.total_sales || 0} orders in last 30 days`}
          accentColor="orange.500"
        />

        <Divider orientation="vertical" height="40px" borderColor={borderColor} />

        <MinimalStatCard
          icon={FiPercent}
          label="Avg Discount"
          number={`${stats?.avg_discount || 0}%`}
          helpText="Average discount across all bundles"
          accentColor="red.500"
        />

        <Divider orientation="vertical" height="40px" borderColor={borderColor} />

        <MinimalStatCard
          icon={FiClock}
          label="Timer Active"
          number={stats?.bundles_with_timer || 0}
          helpText={`${((stats?.bundles_with_timer / stats?.total_bundles) * 100).toFixed(1)}% bundles using timer`}
          accentColor="cyan.500"
        />
      </Flex>
    </Box>
  );
};

export default BundleStats;